import React, { createContext, useContext, useState, useEffect, useRef } from 'react'
import { cn } from '../../utils/cn'
import { ChevronDown, Check } from 'lucide-react'

const SelectContext = createContext()

const Select = ({ value, defaultValue, onValueChange, disabled, children }) => {
  const [internalValue, setInternalValue] = useState(defaultValue)
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef(null)

  const currentValue = value !== undefined ? value : internalValue

  const handleValueChange = (newValue) => {
    if (disabled) return
    if (onValueChange) {
      onValueChange(newValue)
    } else {
      setInternalValue(newValue)
    }
    setIsOpen(false)
  }

  useEffect(() => {
    if (!isOpen) return undefined

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  return (
    <SelectContext.Provider value={{
      value: currentValue,
      onValueChange: handleValueChange,
      disabled,
      isOpen: disabled ? false : isOpen,
      setIsOpen: disabled ? () => {} : setIsOpen,
      containerRef
    }}>
      <div ref={containerRef} className="relative w-full">
        {children}
      </div>
    </SelectContext.Provider>
  )
}

const SelectTrigger = React.forwardRef(({ className, children, disabled, ...props }, ref) => {
  const context = useContext(SelectContext)
  const isDisabled = disabled || context.disabled || props.disabled

  return (
    <button
      ref={ref}
      type="button"
      disabled={isDisabled}
      className={cn(
        "flex h-9 sm:h-10 w-full items-center justify-between rounded-md border border-input bg-background px-2 sm:px-3 py-2 text-xs sm:text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      onClick={() => !isDisabled && context.setIsOpen(!context.isOpen)}
      {...props}
    >
      {children}
      <ChevronDown className={cn("h-3 w-3 sm:h-4 sm:w-4 opacity-50 transition-transform duration-150", context.isOpen && "rotate-180")} />
    </button>
  )
})
SelectTrigger.displayName = "SelectTrigger"

const SelectValue = ({ placeholder, children }) => {
  const context = useContext(SelectContext)

  return (
    <span className="block truncate text-xs sm:text-sm">
      {children || context.value || placeholder}
    </span>
  )
}

const SelectContent = React.forwardRef(({ className, children, side = "auto", ...props }, ref) => {
  const context = useContext(SelectContext)
  const [placement, setPlacement] = useState(side === 'top' ? 'top' : 'bottom')
  const contentRef = useRef(null)

  useEffect(() => {
    if (!context?.isOpen) return

    if (side === 'top' || side === 'bottom') {
      setPlacement(side)
      return
    }

    // Auto-detect whether dropdown should open upwards or downwards
    const containerEl = context?.containerRef?.current
    if (!containerEl) return

    const rect = containerEl.getBoundingClientRect()
    let spaceBelow = window.innerHeight - rect.bottom
    let spaceAbove = rect.top

    let parent = containerEl.parentElement
    while (parent && parent !== document.body && parent !== document.documentElement) {
      const style = window.getComputedStyle(parent)
      const overflowY = style.overflowY || style.overflow
      if (overflowY === 'hidden' || overflowY === 'auto' || overflowY === 'scroll') {
        const parentRect = parent.getBoundingClientRect()
        const parentSpaceBelow = parentRect.bottom - rect.bottom
        const parentSpaceAbove = rect.top - parentRect.top
        if (parentSpaceBelow < spaceBelow) spaceBelow = parentSpaceBelow
        if (parentSpaceAbove < spaceAbove) spaceAbove = parentSpaceAbove
      }
      parent = parent.parentElement
    }

    if (spaceBelow < 180 && spaceAbove > spaceBelow) {
      setPlacement('top')
    } else {
      setPlacement('bottom')
    }
  }, [context?.isOpen, side, context?.containerRef])

  if (!context?.isOpen) return null

  const isTop = placement === 'top'

  return (
    <div
      ref={(node) => {
        contentRef.current = node
        if (typeof ref === 'function') ref(node)
        else if (ref) ref.current = node
      }}
      className={cn(
        "absolute z-50 left-0 right-0 min-w-full max-h-60 overflow-y-auto rounded-md border border-gray-200 bg-white text-popover-foreground shadow-lg duration-100",
        isTop ? "bottom-full mb-1.5 animate-in fade-in-0 slide-in-from-bottom-1" : "top-full mt-1.5 animate-in fade-in-0 slide-in-from-top-1",
        className
      )}
      {...props}
    >
      <div className="p-1">
        {children}
      </div>
    </div>
  )
})
SelectContent.displayName = "SelectContent"

const SelectItem = React.forwardRef(({ className, children, value, disabled = false, ...props }, ref) => {
  const context = useContext(SelectContext)
  const isSelected = context?.value === value

  return (
    <div
      ref={ref}
      className={cn(
        "relative flex w-full cursor-pointer select-none items-center rounded-sm py-2 pl-7 pr-3 text-xs sm:text-sm outline-none transition-colors",
        disabled 
          ? "opacity-40 cursor-not-allowed bg-gray-50/50 text-gray-400 pointer-events-none" 
          : isSelected
            ? "bg-primary/10 text-primary font-medium"
            : "focus:bg-accent focus:text-accent-foreground hover:bg-accent hover:text-accent-foreground text-gray-700",
        className
      )}
      onClick={(e) => {
        if (disabled) {
          e.preventDefault()
          e.stopPropagation()
          return
        }
        context.onValueChange(value)
      }}
      aria-disabled={disabled}
      {...props}
    >
      {isSelected && (
        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center text-primary">
          <Check className="h-3.5 w-3.5" />
        </span>
      )}
      <span className="truncate">{children}</span>
    </div>
  )
})
SelectItem.displayName = "SelectItem"

export { Select, SelectContent, SelectItem, SelectTrigger, SelectValue }